import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { CURRENT_NETWORK } from '../config/network';
import { AMSG_CONTRACT_ADDRESS } from '../config/contract';
import { AMSG_ABI, USDT_ABI } from '../config/abi';
import { pickProvider, detectProvider } from '../utils/wallet';
import { notify } from '../utils/notify';
import { parseContractError } from '../utils/errors';

const Web3Context = createContext(null);
export const useWeb3 = () => useContext(Web3Context);

const STORAGE_KEY = 'amsg_wallet_key';

// chainId can be a hex string ("0x61"), decimal string ("97"), or number.
// parseInt(hexString, 10) returns 0, so we must detect the base ourselves.
const parseChainId = (cid) => {
  if (cid === null || cid === undefined) return null;
  if (typeof cid === 'number') return cid;
  const s = String(cid);
  return s.startsWith('0x') || s.startsWith('0X')
    ? parseInt(s, 16)
    : parseInt(s, 10);
};

export const Web3Provider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contract, setContract] = useState(null);
  const [usdtContract, setUsdtContract] = useState(null);
  const [userId, setUserId] = useState(0);
  const [ownerAddr, setOwnerAddr] = useState(null);
  const [adminAddr, setAdminAddr] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [switching, setSwitching] = useState(false);

  const rawProviderRef = useRef(null);
  const providerRef = useRef(null);
  const listenersRef = useRef(null);
  const initialized = useRef(false);

  const chainIdNum = parseChainId(chainId);
  const onCorrectNetwork = chainIdNum !== null && chainIdNum === CURRENT_NETWORK.chainId;

  const isOwner = !!(account && ownerAddr && account.toLowerCase() === ownerAddr.toLowerCase());
  const isAdmin = !!(account && adminAddr && account.toLowerCase() === adminAddr.toLowerCase());
  const isPrivileged = isOwner || isAdmin;

  const buildContracts = (web3Provider, addr) => {
    if (!web3Provider || !addr) return;
    const sgn = web3Provider.getSigner();
    const amsg = new ethers.Contract(AMSG_CONTRACT_ADDRESS, AMSG_ABI, sgn);
    const usdt = new ethers.Contract(CURRENT_NETWORK.usdtAddress, USDT_ABI, sgn);
    setSigner(sgn);
    setContract(amsg);
    setUsdtContract(usdt);
    fetchUserId(amsg, addr);
    fetchRoles(amsg);
  };

  const fetchUserId = async (amsg, addr) => {
    try {
      const id = await amsg.addressToId(addr);
      setUserId(Number(id.toString()));
    } catch (e) {
      setUserId(0);
    }
  };

  const fetchRoles = async (amsg) => {
    try {
      const [o, a] = await Promise.all([amsg.owner(), amsg.admin()]);
      setOwnerAddr(o);
      setAdminAddr(a);
    } catch (e) {
      setOwnerAddr(null);
      setAdminAddr(null);
    }
  };

  const switchToCorrectNetwork = async (rawProvArg) => {
    const p = rawProvArg || rawProviderRef.current;
    if (!p) return false;
    setSwitching(true);
    try {
      await p.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: CURRENT_NETWORK.chainIdHex }]
      });
      setChainId(CURRENT_NETWORK.chainIdHex);
      setSwitching(false);
      return true;
    } catch (err) {
      const needsAdd =
        err.code === 4902 ||
        err.code === -32603 ||
        (err.data && err.data.originalError && err.data.originalError.code === 4902);

      if (needsAdd) {
        try {
          await p.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: CURRENT_NETWORK.chainIdHex,
              chainName: CURRENT_NETWORK.name,
              nativeCurrency: CURRENT_NETWORK.nativeCurrency,
              rpcUrls: CURRENT_NETWORK.rpcUrls,
              blockExplorerUrls: [CURRENT_NETWORK.blockExplorerUrl]
            }]
          });
          setChainId(CURRENT_NETWORK.chainIdHex);
          setSwitching(false);
          return true;
        } catch (addErr) {
          notify.error('Please add ' + CURRENT_NETWORK.displayName + ' network to your wallet');
          setSwitching(false);
          return false;
        }
      }

      if (err.code === 4001) {
        notify.warning('Network switch was rejected');
      } else {
        notify.error('Failed to switch network');
      }
      setSwitching(false);
      return false;
    }
  };

  const detachListeners = (rawProv) => {
    if (!rawProv || !listenersRef.current) return;
    const { onAccounts, onChain, onDisconnect } = listenersRef.current;
    if (typeof rawProv.removeListener === 'function') {
      try { rawProv.removeListener('accountsChanged', onAccounts); } catch (e) {}
      try { rawProv.removeListener('chainChanged', onChain); } catch (e) {}
      try { rawProv.removeListener('disconnect', onDisconnect); } catch (e) {}
    }
    listenersRef.current = null;
  };

  const attachListeners = (rawProv) => {
    if (!rawProv || typeof rawProv.on !== 'function') return;
    detachListeners(rawProv);

    const onAccounts = (accs) => {
      if (!accs || accs.length === 0) {
        disconnect();
        return;
      }
      setAccount(accs[0]);
      if (providerRef.current) {
        buildContracts(providerRef.current, accs[0]);
      }
      notify.info('Account changed');
    };

    const onChain = (cid) => {
      setChainId(cid);
      const cidNum = parseChainId(cid);
      if (cidNum !== CURRENT_NETWORK.chainId) {
        notify.warning('Wrong network. Switching to ' + CURRENT_NETWORK.displayName + '...');
        switchToCorrectNetwork(rawProv);
      } else {
        notify.success('Connected to ' + CURRENT_NETWORK.displayName);
        // Provider was created with 'any' network, so it adapts automatically.
        // Refresh contract bindings (new signer instance for the new chain).
        rawProv.request({ method: 'eth_accounts' }).then((accs) => {
          if (accs && accs[0] && providerRef.current) {
            buildContracts(providerRef.current, accs[0]);
          }
        }).catch(() => {});
      }
    };

    const onDisconnect = () => disconnect();

    rawProv.on('accountsChanged', onAccounts);
    rawProv.on('chainChanged', onChain);
    rawProv.on('disconnect', onDisconnect);

    listenersRef.current = { onAccounts, onChain, onDisconnect };
  };

  const finalizeConnection = async (rawProv, walletKey) => {
    rawProviderRef.current = rawProv;

    // Create the ethers Web3Provider ONCE per connection with 'any' network —
    // it auto-adapts to chain changes without needing a new instance.
    let web3Provider = providerRef.current;
    if (!web3Provider) {
      web3Provider = new ethers.providers.Web3Provider(rawProv, 'any');
      providerRef.current = web3Provider;
      setProvider(web3Provider);
    }

    attachListeners(rawProv);

    const existing = await rawProv.request({ method: 'eth_accounts' });
    let addr;
    if (!existing || existing.length === 0) {
      const requested = await rawProv.request({ method: 'eth_requestAccounts' });
      if (!requested || requested.length === 0) {
        notify.error('No accounts available');
        return;
      }
      addr = requested[0];
    } else {
      addr = existing[0];
    }
    setAccount(addr);

    const cid = await rawProv.request({ method: 'eth_chainId' });
    setChainId(cid);

    const cidNum = parseChainId(cid);
    if (cidNum !== CURRENT_NETWORK.chainId) {
      await switchToCorrectNetwork(rawProv);
    }

    buildContracts(web3Provider, addr);

    if (walletKey) localStorage.setItem(STORAGE_KEY, walletKey);
  };

  const connect = async (walletKey) => {
    if (connecting) return;
    setConnecting(true);
    try {
      const rawProv = walletKey ? pickProvider(walletKey) : detectProvider();
      if (!rawProv) {
        notify.error('No wallet detected. Please install MetaMask or Trust Wallet.');
        setConnecting(false);
        return;
      }
      await rawProv.request({ method: 'eth_requestAccounts' });
      await finalizeConnection(rawProv, walletKey || 'injected');
      notify.success('Wallet connected');
    } catch (err) {
      notify.error(parseContractError(err));
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = () => {
    detachListeners(rawProviderRef.current);
    setAccount(null);
    setChainId(null);
    setProvider(null);
    setSigner(null);
    setContract(null);
    setUsdtContract(null);
    setUserId(0);
    setOwnerAddr(null);
    setAdminAddr(null);
    rawProviderRef.current = null;
    providerRef.current = null;
    localStorage.removeItem(STORAGE_KEY);
  };

  const refreshUserId = async () => {
    if (!contract || !account) return;
    await fetchUserId(contract, account);
  };

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;

    const rawProv = pickProvider(saved) || detectProvider();
    if (!rawProv) return;

    rawProv.request({ method: 'eth_accounts' })
      .then((accs) => {
        if (accs && accs.length > 0) {
          finalizeConnection(rawProv, saved);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      })
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
      });
  }, []);

  const value = {
    account,
    chainId,
    provider,
    signer,
    contract,
    usdtContract,
    userId,
    ownerAddr,
    adminAddr,
    isOwner,
    isAdmin,
    isPrivileged,
    connecting,
    switching,
    onCorrectNetwork,
    connect,
    disconnect,
    switchToCorrectNetwork: () => switchToCorrectNetwork(rawProviderRef.current),
    refreshUserId,
    network: CURRENT_NETWORK
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
};
